// Live's transitions (#240, spec E2 decision 29): the 157 of its Transitions tab, by its ten
// categories in its order, each with its name as live shows it (some are raw shader names, such
// as "AdvancedMosaic" or live's own "ZoomRigthWipe"). Our keys are the names' slugs, each the
// contract's `TransitionKind` (tsc holds every row to it), and the editor draws them all. A row's third
// column names its `gl-transitions` shader (101 of them, as live draws them); the rest are live's
// own, which "Custom" marks, as its Transition Settings badge does, and which we write ourselves.
import type { TransitionKind } from "../../api/generated/model";

export type TransitionCategoryId =
  | "camera"
  | "light"
  | "blur"
  | "3d"
  | "glitch"
  | "distort"
  | "classic"
  | "wipe"
  | "geometric"
  | "stylized";

export interface TransitionInfo {
  key: string;
  name: string;
  category: TransitionCategoryId;
  /** Live's own shader: its Transition Settings card carries a "Custom" badge. */
  custom: boolean;
  /** The kind the editor draws: its key, as the contract names it. */
  kind: TransitionKind;
  /** Its `gl-transitions` shader's name, or null for one of live's own. */
  glName: string | null;
}

interface CategoryRow {
  id: TransitionCategoryId;
  label: string;
  names: readonly (readonly [key: TransitionKind, name: string, glName?: string])[];
}

const ROWS: readonly CategoryRow[] = [
  {
    id: "camera",
    label: "Camera",
    names: [
      ["bounce", "Bounce", "Bounce"],
      ["cross-zoom", "Cross Zoom", "CrossZoom"],
      ["directional-slide", "Directional Slide", "Directional"],
      ["dreamy-zoom", "Dreamy Zoom", "DreamyZoom"],
      ["mosaic-slide", "Mosaic Slide", "Mosaic"],
      ["simple-zoom", "Simple Zoom", "SimpleZoom"],
      ["zoom-in-circles", "Zoom In Circles", "ZoomInCircles"],
      ["directional-warp", "Directional Warp", "directionalwarp"],
      ["rotate-scale-fade", "Rotate Scale Fade", "rotate_scale_fade"],
      ["dynamic-zoom-in", "Dynamic Zoom In"],
      ["dynamic-zoom-out", "Dynamic Zoom Out"],
      ["spin-360", "Spin 360°"],
      ["spin-reverse", "Spin Reverse"],
      ["whip-pan-right", "Whip Pan Right"],
      ["whip-pan-left", "Whip Pan Left"],
      ["whip-pan-up", "Whip Pan Up"],
      ["whip-pan-down", "Whip Pan Down"],
      ["crash-zoom-spin", "Crash Zoom Spin"],
      ["pendulum-swing", "Pendulum Swing"],
      ["snap-push-in", "Snap Push In"],
    ],
  },
  {
    id: "light",
    label: "Light",
    names: [
      ["film-burn", "Film Burn"],
      ["burn", "Burn", "burn"],
      ["color-phase", "Color Phase", "colorphase"],
      ["fade-through-color", "Fade Through Color", "fadecolor"],
      ["undulating-burn-out", "Undulating Burn Out", "undulatingBurnOut"],
      ["flash-white", "Flash White"],
      ["flash-black", "Flash Black"],
      ["exposure-spike", "Exposure Spike"],
      ["prismatic-light-leak", "Prismatic Light Leak"],
      ["neon-strobe-burn", "Neon Strobe Burn"],
      ["supernova-burst", "Supernova Burst"],
    ],
  },
  {
    id: "blur",
    label: "Blur",
    names: [
      ["dreamy", "Dreamy", "Dreamy"],
      ["linear-blur", "Linear Blur", "LinearBlur"],
      ["morph", "Morph", "morph"],
      ["radial-zoom-blur", "Radial Zoom Blur"],
      ["lens-defocus", "Lens Defocus"],
      ["directional-motion-blur", "Directional Motion Blur"],
      ["hexagonal-bokeh-blur", "Hexagonal Bokeh Blur"],
      ["tilt-shift-morph", "Tilt-Shift Morph"],
    ],
  },
  {
    id: "3d",
    label: "3D",
    names: [
      ["inverted-page-curl", "Inverted Page Curl", "InvertedPageCurl"],
      ["stereo-viewer", "Stereo Viewer", "StereoViewer"],
      ["3d-cube", "3D Cube", "cube"],
      ["doorway", "Doorway", "doorway"],
      ["swap", "Swap", "swap"],
      ["3d-card-flip", "3D Card Flip"],
      ["3d-page-turn", "3D Page Turn"],
      ["3d-origami-fold", "3D Origami Fold"],
      ["3d-flip-up", "3D Flip Up"],
      ["3d-cube-spin", "3D Cube Spin"],
    ],
  },
  {
    id: "glitch",
    label: "Glitch & Pixel",
    names: [
      ["glitch-displace", "Glitch Displace", "GlitchDisplace"],
      ["glitch-memories", "Glitch Memories", "GlitchMemories"],
      ["pixelize", "Pixelize", "pixelize"],
      ["random-squares", "Random Squares", "randomsquares"],
      ["rgb-split", "RGB Split"],
      ["vhs-tracking", "VHS Tracking"],
      ["digital-block-tear", "Digital Block Tear"],
      ["vhs-rewind", "VHS Rewind"],
      ["matrix-pixel-rain", "Matrix Pixel Rain"],
    ],
  },
  {
    id: "distort",
    label: "Distort",
    names: [
      ["butterfly-wave", "Butterfly Wave", "ButterflyWaveScrawler"],
      ["crazy-parametric", "Crazy Parametric", "CrazyParametricFun"],
      ["swirl", "Swirl", "Swirl"],
      ["water-drop", "Water Drop", "WaterDrop"],
      ["cross-warp", "Cross Warp", "crosswarp"],
      ["displacement-map", "Displacement Map", "displacement"],
      ["fly-eye", "Fly Eye", "flyeye"],
      ["kaleidoscope", "Kaleidoscope", "kaleidoscope"],
      ["perlin-noise", "Perlin Noise", "perlin"],
      ["ripple", "Ripple", "ripple"],
      ["water-ripple", "Water Ripple"],
      ["black-hole-vortex", "Black Hole Vortex"],
      ["refractive-shockwave", "Refractive Shockwave"],
      ["liquid-slime-melt", "Liquid Slime Melt"],
    ],
  },
  {
    id: "classic",
    label: "Classic",
    names: [
      ["advanced-mosaic", "AdvancedMosaic", "AdvancedMosaic"],
      ["block-dissolve", "BlockDissolve", "BlockDissolve"],
      ["book-flip", "BookFlip", "BookFlip"],
      ["box", "Box", "Box"],
      ["defocus-blur", "DefocusBlur", "DefocusBlur"],
      ["directional-scaled", "DirectionalScaled", "DirectionalScaled"],
      ["edge-transition", "EdgeTransition", "EdgeTransition"],
      ["fold", "Fold", "Fold"],
      ["hsvfade", "HSVfade", "HSVfade"],
      ["horizontal-close", "HorizontalClose", "HorizontalClose"],
      ["horizontal-open", "HorizontalOpen", "HorizontalOpen"],
      ["left-right", "LeftRight", "LeftRight"],
      ["overexposure", "Overexposure", "Overexposure"],
      ["rectangle", "Rectangle", "Rectangle"],
      ["rectangle-crop", "RectangleCrop", "RectangleCrop"],
      ["simple-flip", "SimpleFlip", "SimpleFlip"],
      ["star-wipe", "StarWipe", "StarWipe"],
      ["static-fade", "StaticFade", "StaticFade"],
      ["tvstatic", "TVStatic", "TVStatic"],
      ["top-bottom", "TopBottom", "TopBottom"],
      ["vertical-close", "VerticalClose", "VerticalClose"],
      ["vertical-open", "VerticalOpen", "VerticalOpen"],
      ["zoom-left-wipe", "ZoomLeftWipe", "ZoomLeftWipe"],
      ["zoom-rigth-wipe", "ZoomRigthWipe", "ZoomRigthWipe"],
      ["chessboard", "chessboard", "chessboard"],
      ["coord-from-in", "coord-from-in", "coord-from-in"],
      ["directional-easing", "directional-easing", "directional-easing"],
      ["fade", "Fade", "fade"],
      ["fade-to-grayscale", "Fade to Grayscale", "fadegrayscale"],
      ["fragment", "fragment", "fragment"],
      ["luma-matte", "Luma Matte", "luma"],
      ["luminance-melt", "Luminance Melt", "luminance_melt"],
      ["mosaic-transition", "mosaic_transition", "mosaic_transition"],
      ["multiply-blend", "Multiply Blend", "multiply_blend"],
      ["parametric-glitch", "parametric_glitch", "parametric_glitch"],
      ["power-kaleido", "powerKaleido", "powerKaleido"],
      ["random-noisex", "randomNoisex", "randomNoisex"],
      ["tangent-motion-blur", "tangentMotionBlur", "tangentMotionBlur"],
      ["x-axis-translation", "x_axis_translation", "x_axis_translation"],
      ["zoom-in-out", "zoomInOut", "zoomInOut"],
      ["smooth-film-dissolve", "Smooth Film Dissolve"],
      ["luma-glow-reveal", "Luma Glow Reveal"],
      ["additive-light-dissolve", "Additive Light Dissolve"],
    ],
  },
  {
    id: "wipe",
    label: "Wipe & Slide",
    names: [
      ["bow-tie-horizontal", "Bow Tie Horizontal", "BowTieHorizontal"],
      ["bow-tie-vertical", "Bow Tie Vertical", "BowTieVertical"],
      ["directional-wipe", "Directional Wipe", "directionalwipe"],
      ["squares-wire", "Squares Wire", "squareswire"],
      ["window-blinds", "Window Blinds", "windowblinds"],
      ["window-slice", "Window Slice", "windowslice"],
      ["wipe-down", "Wipe Down", "wipeDown"],
      ["wipe-left", "Wipe Left", "wipeLeft"],
      ["wipe-right", "Wipe Right", "wipeRight"],
      ["wipe-up", "Wipe Up", "wipeUp"],
      ["diagonal-slice", "Diagonal Slice"],
      ["curtain-split", "Curtain Split"],
      ["cross-split", "Cross Split"],
      ["multi-stripe-slice", "Multi-Stripe Slice"],
      ["radial-clock-wipe", "Radial Clock Wipe"],
      ["venetian-blinds", "Venetian Blinds"],
    ],
  },
  {
    id: "geometric",
    label: "Shapes",
    names: [
      ["circle-crop", "Circle Crop", "CircleCrop"],
      ["grid-flip", "Grid Flip", "GridFlip"],
      ["polka-dots-curtain", "Polka Dots Curtain", "PolkaDotsCurtain"],
      ["radial-wipe", "Radial Wipe", "Radial"],
      ["angular", "Angular", "angular"],
      ["circle", "Circle", "circle"],
      ["circle-open", "Circle Open", "circleopen"],
      ["crosshatch", "Crosshatch", "crosshatch"],
      ["heart", "Heart", "heart"],
      ["hexagonalize", "Hexagonalize", "hexagonalize"],
      ["pinwheel", "Pinwheel", "pinwheel"],
      ["polar-function", "Polar Function", "polar_function"],
      ["iris-circle", "Iris Circle"],
      ["diamond-iris", "Diamond Iris"],
      ["star-reveal", "Star Reveal"],
      ["heart-iris-mask", "Heart Iris Mask"],
      ["concentric-rings", "Concentric Rings"],
      ["hexagon-iris", "Hexagon Iris"],
      ["radial-swipe", "Radial Swipe"],
    ],
  },
  {
    id: "stylized",
    label: "Whimsical",
    names: [
      ["colour-distance", "Colour Distance", "ColourDistance"],
      ["doom", "Doom", "DoomScreenTransition"],
      ["cannabis-leaf", "Cannabis Leaf", "cannabisleaf"],
      ["squeeze", "Squeeze", "squeeze"],
      ["pop-art-halftone", "Pop Art Halftone"],
      ["painterly-brush-stroke", "Painterly Brush Stroke"],
      ["sci-fi-laser-scan", "Sci-Fi Laser Scan"],
    ],
  },];

const CUSTOM: ReadonlySet<string> = new Set([
  "3d-card-flip", "3d-cube-spin", "3d-flip-up", "3d-origami-fold", "3d-page-turn",
  "additive-light-dissolve", "black-hole-vortex", "concentric-rings", "crash-zoom-spin",
  "cross-split", "curtain-split", "diagonal-slice", "diamond-iris", "digital-block-tear",
  "directional-motion-blur", "dynamic-zoom-in", "dynamic-zoom-out", "exposure-spike", "film-burn",
  "flash-black", "flash-white", "heart-iris-mask", "hexagon-iris", "hexagonal-bokeh-blur",
  "iris-circle", "lens-defocus", "liquid-slime-melt", "luma-glow-reveal", "matrix-pixel-rain",
  "multi-stripe-slice", "neon-strobe-burn", "painterly-brush-stroke", "pendulum-swing",
  "pop-art-halftone", "prismatic-light-leak", "radial-clock-wipe", "radial-swipe",
  "radial-zoom-blur", "refractive-shockwave", "rgb-split", "sci-fi-laser-scan",
  "smooth-film-dissolve", "snap-push-in", "spin-360", "spin-reverse", "star-reveal",
  "supernova-burst", "tilt-shift-morph", "venetian-blinds", "vhs-rewind", "vhs-tracking",
  "water-ripple", "whip-pan-down", "whip-pan-left", "whip-pan-right", "whip-pan-up",
]);
/** Live's categories, in its order, with their labels (its tab's rows and its panel's pills). */
export const TRANSITION_CATEGORIES: readonly { id: TransitionCategoryId; label: string }[] = ROWS.map(
  ({ id, label }) => ({ id, label }),
);

/** Every transition of live's tab, category by category. */
export const TRANSITIONS: readonly TransitionInfo[] = ROWS.flatMap(({ id, names }) =>
  names.map(([key, name, glName]) => ({
    key,
    name,
    category: id,
    custom: CUSTOM.has(key),
    kind: key,
    glName: glName ?? null,
  })),
);

/** What a tile dragged to the timeline carries: the key of its transition. */
export const TRANSITION_DRAG_TYPE = "application/x-cai-transition";

/** The kind a dragged tile's key names, or null for a key the catalog does not know. */
export function drawnKind(key: string): TransitionKind | null {
  return TRANSITIONS.find((each) => each.key === key)?.kind ?? null;
}

/** A drawn kind's `gl-transitions` shader, or null for one of live's own. */
export function glNameOf(kind: TransitionKind): string | null {
  return TRANSITIONS.find((each) => each.key === kind)?.glName ?? null;
}

/** A drawn kind's name, as live shows it. */
export function transitionName(kind: TransitionKind): string {
  return TRANSITIONS.find((each) => each.key === kind)?.name ?? kind;
}

/** Live's search: the transitions whose name holds the words typed, in any case. */
export function searchTransitions(
  list: readonly TransitionInfo[],
  query: string,
): readonly TransitionInfo[] {
  const words = query.trim().toLowerCase();
  return words === "" ? list : list.filter((each) => each.name.toLowerCase().includes(words));
}
